import { useState } from "react";
import type { Movie } from "@shared/types";
import { SafeCover } from "./SafeCover";

export function MovieConfirm({
  movie,
  onSave,
  onCancel,
}: {
  movie: Movie;
  onSave: (m: Movie) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(movie.title);
  const [director, setDirector] = useState(movie.director ?? "");
  const [year, setYear] = useState(movie.year?.toString() ?? "");
  const [format, setFormat] = useState(movie.format);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSave({
        ...movie,
        uuid: movie.uuid || crypto.randomUUID(),
        title: title.trim(),
        director: director.trim() || null,
        year: year.trim() ? Number(year) : null,
        format,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <SafeCover url={movie.cover_url} alt={movie.title} className="w-20 h-28" />
        <div className="flex-1 text-sm text-gray-400 space-y-1">
          {movie.genre && <div>{movie.genre}</div>}
          {movie.runtime && <div>{movie.runtime} min</div>}
          {movie.media_type === "series" && movie.seasons && (
            <div>{movie.seasons} season{movie.seasons === 1 ? "" : "s"}</div>
          )}
        </div>
      </div>
      <Field label="Title">
        <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Director">
        <input value={director} onChange={(e) => setDirector(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Year">
        <input
          type="number"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Format">
        <select value={format} onChange={(e) => setFormat(e.target.value)} className={inputCls}>
          <option value="Blu-ray">Blu-ray</option>
          <option value="DVD">DVD</option>
          <option value="4K UHD">4K UHD</option>
          <option value="Digital">Digital</option>
        </select>
      </Field>
      <div className="flex gap-2 pt-2">
        <button
          onClick={submit}
          disabled={submitting || !title.trim()}
          className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:text-gray-300 rounded font-medium"
        >
          {submitting ? "Saving…" : "Save"}
        </button>
        <button onClick={onCancel} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded">
          Cancel
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-sm text-gray-300">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500";
